import { gql } from '@apollo/client';
import { PROGRAM_FIELDS } from '@src/graphql/queries/program';
// Member-facing query - checks current user's duplicates only
export const CHECK_MY_DUPLICATE_PAYMENT_REQUEST_ITEMS = gql `
  query CHECK_MY_DUPLICATE_PAYMENT_REQUEST_ITEMS($spec: CheckMyDuplicatePaymentRequestSpec!) {
    checkMyDuplicatePaymentRequestItems(spec: $spec) {
      tempId
      duplicates {
        paymentRequestId
        requestNum
        submittedAt
        status
        termStartDate
        termEndDate
        program {
          id
          programName
          fieldOfStudyName
          otherFieldOfStudy
          startDate
          estCompDate
          schoolName
          otherSchool
        }
        paymentRequestItem {
          id
          type
          title
          courseCode
          status
          amountRequested
          amountApproved
          amountToPay
          feeType {
            name
          }
          extraData
          calculations
        }
      }
    }
  }
`;
// Admin-facing query - can check any user's duplicates
export const CHECK_DUPLICATE_PAYMENT_REQUEST_ITEMS = gql `
  query CHECK_DUPLICATE_PAYMENT_REQUEST_ITEMS($spec: CheckDuplicatePaymentRequestSpec!) {
    checkDuplicatePaymentRequestItems(spec: $spec) {
      tempId
      duplicates {
        paymentRequestId
        requestNum
        submittedAt
        status
        termStartDate
        termEndDate
        program {
          id
          programName
          fieldOfStudyName
          otherFieldOfStudy
          startDate
          estCompDate
          schoolName
          otherSchool
        }
        paymentRequestItem {
          id
          type
          title
          courseCode
          status
          amountRequested
          amountApproved
          amountToPay
          feeType {
            name
          }
          extraData
          calculations
        }
      }
    }
  }
`;
export const PAYMENT_REQUEST_FIELDS = gql `
  fragment PaymentRequestFields on PaymentRequest {
    id
    createdAt
    updatedAt
    clientId
    status
    programId
    amountRequested
    amountApproved
    amountToPay
    amountEarmarked
    approvedDate
    processedDate
    submittedAt
    capYear
    capSegment
    budgetYear
    budgetSegment
    requestNum
    workflowRunId
    termName
    termStartDate
    termEndDate
    requiresExpenses
    inProcess
    documents {
      docs {
        s3Key
        name
        date
        fileSize
      }
    }
    processedById
    processedBy {
      id
      firstName
      lastName
      email
    }
    cgpa
    creditsEarnedSoFar
    paymentMethod
    preimbursementStatus
    extraData
  }
`;
export const PAYMENT_REQUEST_ITEM_FIELDS = gql `
  fragment PaymentRequestItemFields on PaymentRequestItem {
    id
    clientId
    createdAt
    updatedAt
    paymentRequestId
    type
    originalAmountRequested
    originalAmountRequestedCurrency
    amountRequested
    amountApproved
    amountToPay
    exchangeRate
    exchangeRateDate
    receiptDate
    capYear
    capSegment
    courseCode
    title
    courseGrade
    courseGradePct
    gradingScaleId
    description
    feeTypeId
    isDeleted
    status
    reason
    documents {
      docs {
        s3Key
        name
        date
        fileSize
      }
    }
    feeType {
      code
      name
    }
    calculations
    extraData
  }
`;
export const SAVE_PAYMENT_REQUEST = gql `
  ${PAYMENT_REQUEST_FIELDS}
  mutation SAVE_PAYMENT_REQUEST($input: SavePaymentRequestInput!) {
    savePaymentRequest(input: $input) {
      success
      paymentRequest {
        ...PaymentRequestFields
      }
      messages {
        value
        type
        params
      }
    }
  }
`;
export const APPROVE_PAYMENT_REQUEST = gql `
  ${PAYMENT_REQUEST_FIELDS}
  ${PAYMENT_REQUEST_ITEM_FIELDS}
  ${PROGRAM_FIELDS}
  mutation APPROVE_PAYMENT_REQUEST($input: ApprovePaymentRequestInput!, $calculateOnly: Boolean!) {
    approvePaymentRequest(input: $input, calculateOnly: $calculateOnly) {
      success
      paymentRequest {
        ...PaymentRequestFields
        client {
          name
          clientConfig {
            currency
          }
        }
        program {
          ...ProgramFields
          employee {
            firstName
            lastName
            email
            uniqueId
            userId
          }
        }
        paymentRequestItems {
          ...PaymentRequestItemFields
        }
      }
      messages {
        value
        type
        params
      }
    }
  }
`;
export const LIST_PAYMENT_REQUEST_ITEMS = gql `
  ${PAYMENT_REQUEST_ITEM_FIELDS}
  query LIST_PAYMENT_REQUEST_ITEMS($id: Int!) {
    paymentRequestItems(id: $id) {
      ...PaymentRequestItemFields
    }
  }
`;
export const PAYMENT_REQUEST_CLAIMS = gql `
  ${PAYMENT_REQUEST_FIELDS}
  ${PROGRAM_FIELDS}
  query PAYMENT_REQUEST_CLAIMS($spec: PaymentRequestClaimsSpec!) {
    paymentRequestClaims(spec: $spec) {
      total
      page
      pageSize
      items {
        ...PaymentRequestFields
        client {
          name
          clientConfig {
            currency
          }
        }
        program {
          ...ProgramFields
          employee {
            firstName
            lastName
            uniqueId
          }
        }
      }
    }
  }
`;
export const PAYMENT_REQUEST_CLAIMS_DOWNLOAD = gql `
  query PAYMENT_REQUEST_CLAIMS_DOWNLOAD($spec: PaymentRequestClaimsSpec!) {
    paymentRequestClaimsDownload(spec: $spec) {
      contents
      rowCount
      totalRowCount
    }
  }
`;
export const UPDATE_PAYMENT_REQUEST_STATUS = gql `
  ${PAYMENT_REQUEST_FIELDS}
  ${PAYMENT_REQUEST_ITEM_FIELDS}
  mutation UPDATE_PAYMENT_REQUEST_STATUS(
    $id: Int!
    $status: PaymentRequestStatus!
    $commentContent: String
  ) {
    updatePaymentRequestStatus(id: $id, status: $status, commentContent: $commentContent) {
      ...PaymentRequestFields
      paymentRequestItems {
        ...PaymentRequestItemFields
      }
    }
  }
`;
export const UPDATE_PAYMENT_REQUEST_DOCUMENTS = gql `
  ${PAYMENT_REQUEST_FIELDS}
  ${PAYMENT_REQUEST_ITEM_FIELDS}
  mutation UPDATE_PAYMENT_REQUEST_DOCUMENTS($id: Int!, $input: DocumentsInput!) {
    updatePaymentRequestDocuments(id: $id, input: $input) {
      ...PaymentRequestFields
      paymentRequestItems {
        ...PaymentRequestItemFields
      }
    }
  }
`;
export const PAYMENT_REQUESTS = gql `
  ${PAYMENT_REQUEST_FIELDS}
  ${PAYMENT_REQUEST_ITEM_FIELDS}
  ${PROGRAM_FIELDS}
  query PAYMENT_REQUESTS($spec: PaymentRequestSpec!) {
    paymentRequests(spec: $spec) {
      ...PaymentRequestFields
      program {
        ...ProgramFields
      }
      paymentRequestItems {
        ...PaymentRequestItemFields
      }
    }
  }
`;
export const PAYMENT_REQUEST = gql `
  ${PAYMENT_REQUEST_FIELDS}
  ${PAYMENT_REQUEST_ITEM_FIELDS}
  ${PROGRAM_FIELDS}
  query PAYMENT_REQUEST($id: Int!) {
    paymentRequest(id: $id) {
      ...PaymentRequestFields
      client {
        name
        clientConfig {
          currency
        }
      }
      program {
        ...ProgramFields
        employee {
          id
          clientId
          firstName
          lastName
          email
          uniqueId
          userId
        }
      }
      paymentRequestItems {
        ...PaymentRequestItemFields
      }
    }
  }
`;
export const PAYMENT_REQUEST_COMMENTS = gql `
  query PAYMENT_REQUEST_COMMENTS($id: Int!) {
    paymentRequestComments(id: $id) {
      status
      empLocale
      isEmployee
      comment {
        id
        message
        isInternal
        createdAt
        updatedAt
        createdBy {
          firstName
          lastName
        }
        extraData
      }
    }
  }
`;
export const SET_PAYMENT_REQUEST_IN_PROCESS = gql `
  ${PAYMENT_REQUEST_FIELDS}
  ${PAYMENT_REQUEST_ITEM_FIELDS}
  mutation SET_PAYMENT_REQUEST_IN_PROCESS($id: Int!, $inProcess: Boolean!) {
    setPaymentRequestInProcess(id: $id, inProcess: $inProcess) {
      __typename
      ... on PaymentRequest {
        ...PaymentRequestFields
        paymentRequestItems {
          ...PaymentRequestItemFields
        }
      }
      ... on MessageResponse {
        success
        messages {
          type
          value
          params
          sender
        }
      }
    }
  }
`;
export const GET_PAYMENT_SUMMARY = gql `
  query GET_PAYMENT_SUMMARY($employeeId: Int!) {
    getPaymentSummary(employeeId: $employeeId) {
      year
      segment
      periodStart
      periodEnd
      isCurrentPeriod
      totalRequested
      totalPaid
      totalPending
      fundingCap
    }
  }
`;
export const GET_PAYMENT_REQUEST_STATUS_HISTORY = gql `
  query GET_PAYMENT_REQUEST_STATUS_HISTORY($id: Int!) {
    getPaymentRequestStatusHistory(id: $id) {
      id
      oldStatus
      newStatus
      updatedAt
      updatedBy {
        id
        firstName
        lastName
      }
    }
  }
`;
